/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { t } from "i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { hasRole } from "../../utils/Helpers";
import { routesList, NavRoute } from "./routes";

const DESKTOP_BREAKPOINT = 992;
const WRAP_ITEM_THRESHOLD = 6;

// Width tiers -> how many nav items to show directly before overflowing to "More".
// Tune these numbers/breakpoints as needed; kept simple (no live measurement).
const VISIBLE_ITEMS_TIERS: Array<{ minWidth: number; count: number }> = [
  { minWidth: 1600, count: 12 },
  { minWidth: 1440, count: 10 },
  { minWidth: 1280, count: 8 },
  { minWidth: DESKTOP_BREAKPOINT, count: 6 },
];

const getMaxVisibleItems = (width: number): number => {
  for (const tier of VISIBLE_ITEMS_TIERS) {
    if (width >= tier.minWidth) return tier.count;
  }
  return VISIBLE_ITEMS_TIERS[VISIBLE_ITEMS_TIERS.length - 1].count;
};

export const NavbarItems: React.FC = () => {
  const navigation = useNavigate();
  const locationObj = useLocation();
  const selectedRoute = locationObj.pathname;
  const [moreOpen, setMoreOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [isDesktop, setIsDesktop] = useState(
    window.innerWidth >= DESKTOP_BREAKPOINT
  );
  const [maxVisibleItems, setMaxVisibleItems] = useState(() =>
    getMaxVisibleItems(window.innerWidth)
  );
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
      setMaxVisibleItems(getMaxVisibleItems(window.innerWidth));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const allowedRoutes = (): NavRoute[] => {
    const arr: NavRoute[] = [];
    routesList.forEach(
      (item) =>
        hasRole(item.role) && item.routes.forEach((routes) => arr.push(routes))
    );
    return arr;
  };

  const routes = allowedRoutes();
  const visibleRoutes = isDesktop
    ? routes.slice(0, maxVisibleItems)
    : routes;
  const hiddenRoutes = isDesktop ? routes.slice(maxVisibleItems) : [];
  const selectedHiddenRoute = hiddenRoutes.find(
    (route) => route.url === selectedRoute
  );

  const openMore = () => {
    const rect = toggleRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({ top: rect.bottom, left: rect.left });
    }
    setMoreOpen((open) => !open);
  };

  useLayoutEffect(() => {
    if (!moreOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !toggleRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    const handleScroll = () => setMoreOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [moreOpen]);

  const shouldWrapItems = visibleRoutes.length > WRAP_ITEM_THRESHOLD;

  const renderNavItem = (route: NavRoute) => (
    <li
      key={route.name}
      className="nav-item"
      style={shouldWrapItems ? { maxWidth: 100 } : undefined}
    >
      <button
        className={
          selectedRoute === route.url
            ? "btn btn-link nav-link selected-btn"
            : "btn btn-link nav-link"
        }
        aria-current="page"
        onClick={() => navigation(route.url)}
        style={shouldWrapItems ? { display: "block", width: "100%" } : undefined}
      >
        {t(route.name).toString()}
      </button>
    </li>
  );

  return (
    <ul
      className="navbar-nav"
      style={isDesktop ? { flexWrap: "nowrap" } : { flexWrap: "wrap" }}
    >
      {visibleRoutes.map(renderNavItem)}
      {hiddenRoutes.length > 0 && (
        <li className="nav-item">
          <button
            ref={toggleRef}
            className={
              "btn btn-link nav-link dropdown-toggle" +
              (selectedHiddenRoute ? " selected-btn" : "")
            }
            onClick={openMore}
          >
            More
          </button>
          {moreOpen &&
            createPortal(
              <ul
                ref={menuRef}
                className="dropdown-menu show"
                style={{
                  position: "fixed",
                  top: menuPos.top,
                  left: menuPos.left,
                }}
              >
                {hiddenRoutes.map((route) => {
                  const isSelected = selectedRoute === route.url;
                  return (
                    <li key={route.name}>
                      <button
                        className={
                          isSelected
                            ? "dropdown-item selected-btn"
                            : "dropdown-item"
                        }
                        style={
                          isSelected
                            ? {
                                backgroundColor: "rgba(245, 179, 53, 0.25)",
                                fontWeight: 600,
                              }
                            : undefined
                        }
                        onClick={() => {
                          navigation(route.url);
                          setMoreOpen(false);
                        }}
                      >
                        {t(route.name).toString()}
                      </button>
                    </li>
                  );
                })}
              </ul>,
              document.body
            )}
        </li>
      )}
    </ul>
  );
};
